import { ConnectedAccountDocumentType } from '~/swagger/pay/Api';
import type { Profile } from '~/contexts/global';
import type { RequestConnectedAccountRequestDto } from '~/swagger/pay/Api';
import type { AddressDto, CompanyDto } from '~/swagger/portal/Api';

export type LinkAccountDetailsFormData = {
  companyBusinessInfoId: string;
  documentUploadSessionIds: string[];
  signerName: string;
  signerEmail: string;
  signerPhone: string;
  signerAddress: {
    streetAddress1: string;
    streetAddress2: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  signerSsn: string;
  signerDateOfBirth: string;
};

export type LinkAgreementFormData = {
  agreementAccepted: boolean;
};

export const emptyAccountForm = (): LinkAccountDetailsFormData => ({
  companyBusinessInfoId: '',
  documentUploadSessionIds: [],
  signerName: '',
  signerEmail: '',
  signerPhone: '',
  signerAddress: {
    streetAddress1: '',
    streetAddress2: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
  },
  signerSsn: '',
  signerDateOfBirth: '',
});

export const emptyAgreementForm = (): LinkAgreementFormData => ({
  agreementAccepted: false,
});

export const normalizeLinkAccountDetailsFormData = (d: Partial<LinkAccountDetailsFormData> | undefined): LinkAccountDetailsFormData => {
  const base = emptyAccountForm();
  const rawAddr: Partial<LinkAccountDetailsFormData['signerAddress']> =
    d?.signerAddress && typeof d.signerAddress === 'object' && !Array.isArray(d.signerAddress) ? d.signerAddress : {};
  return {
    companyBusinessInfoId: typeof d?.companyBusinessInfoId === 'string' ? d.companyBusinessInfoId : '',
    documentUploadSessionIds: Array.isArray(d?.documentUploadSessionIds)
      ? d.documentUploadSessionIds.filter((item): item is string => typeof item === 'string')
      : [],
    signerName: typeof d?.signerName === 'string' ? d.signerName : '',
    signerEmail: typeof d?.signerEmail === 'string' ? d.signerEmail : '',
    signerPhone: typeof d?.signerPhone === 'string' ? d.signerPhone : '',
    signerAddress: {
      streetAddress1: typeof rawAddr.streetAddress1 === 'string' ? rawAddr.streetAddress1 : '',
      streetAddress2: typeof rawAddr.streetAddress2 === 'string' ? rawAddr.streetAddress2 : '',
      city: typeof rawAddr.city === 'string' ? rawAddr.city : '',
      state: typeof rawAddr.state === 'string' ? rawAddr.state : '',
      zipCode: typeof rawAddr.zipCode === 'string' ? rawAddr.zipCode : '',
      country: typeof rawAddr.country === 'string' ? rawAddr.country : base.signerAddress.country,
    },
    signerSsn: typeof d?.signerSsn === 'string' ? d.signerSsn : '',
    signerDateOfBirth: typeof d?.signerDateOfBirth === 'string' ? d.signerDateOfBirth : '',
  };
};

export const cloneLinkAccountDetailsFormData = (
  d: Partial<LinkAccountDetailsFormData> | LinkAccountDetailsFormData
): LinkAccountDetailsFormData => normalizeLinkAccountDetailsFormData(d);

const emptySignerAddress = (): LinkAccountDetailsFormData['signerAddress'] => ({ ...emptyAccountForm().signerAddress });

const signerAddressFromDto = (addr: AddressDto | undefined | null): LinkAccountDetailsFormData['signerAddress'] => {
  if (!addr) return emptySignerAddress();
  return {
    streetAddress1: addr.streetAddress1 ?? '',
    streetAddress2: addr.streetAddress2 ?? '',
    city: addr.city ?? '',
    state: addr.state ?? '',
    zipCode: addr.zipCode ?? '',
    country: addr.country ?? '',
  };
};

export const accountDefaultsFromPmAndCompany = (
  pm: Profile | null | undefined,
  company: CompanyDto | null | undefined
): LinkAccountDetailsFormData => {
  return normalizeLinkAccountDetailsFormData({
    companyBusinessInfoId: '',
    signerName: pm?.name ?? '',
    signerEmail: pm?.email ?? '',
    signerPhone: pm?.phone ?? '',
    signerAddress: signerAddressFromDto(company?.address),
    signerSsn: '',
    signerDateOfBirth: '',
  });
};

export const toRequestConnectedAccountPayload = (input: {
  bankAccountId: string;
  form: LinkAccountDetailsFormData;
  accepted: string;
}): RequestConnectedAccountRequestDto => {
  const { form, bankAccountId, accepted } = input;
  const a = form.signerAddress;
  return {
    bankAccountId,
    companyBusinessInfoId: form.companyBusinessInfoId,
    documents: form.documentUploadSessionIds.map((uploadSessionId) => ({
      type: ConnectedAccountDocumentType.BankStatement,
      uploadSessionId,
    })),
    signerName: form.signerName,
    signerEmail: form.signerEmail,
    signerPhone: form.signerPhone,
    signerAddress: {
      streetAddress1: a.streetAddress1,
      streetAddress2: a.streetAddress2 || undefined,
      city: a.city,
      state: a.state,
      zipCode: a.zipCode,
      country: a.country || undefined,
    },
    signerSsn: form.signerSsn.replace(/-/g, ''),
    signerDateOfBirth: form.signerDateOfBirth,
    accepted,
  };
};
